import { useState } from 'react';

import { FiCalendar } from 'react-icons/fi';
import { useParams } from 'react-router-dom';

import {
  Box,
  Radio,
  Drawer,
  Checkbox,
  FormLabel,
  FormControl,
  IconButton,
  RadioGroup,
  Typography,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Divider,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { DatePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import { NonFunctionKeys } from 'utility-types';

import { PeriodTypes, AccountingPortfolioTypes } from '@graphql/query/portfolio';

import TextField from '~/DEPRECATED/components/Filter/TextField';
import useColorMode from '~/hooks/useColorMode';
import usePortfolio, { PortfolioHookProps, PortfolioProps } from '~/pages/Portfolio/usePortfolio';
import usePortfolioFilter from '~/pages/Portfolio/usePortfolioFilter';

import ContractFlagFilter from './ContractFlagFilter';
import Summary from './Summary';
import useStyles from './styles';

interface CheckboxOptionProps {
  name: string;
  key: keyof PortfolioProps;
  value: string;
}

const INCENTIVIZED_ENERGY_TYPES = ['I5', 'I8', 'I1', 'I0', 'INE5'];
const CONVENTIONAL_ENERGY_TYPES = ['C'];
const MODULATION_OPTIONS: CheckboxOptionProps[] = [
  { name: 'CARGA', key: 'modulation', value: 'CARGA' },
  { name: 'DECLARADA', key: 'modulation', value: 'DECLARADA' },
  { name: 'GERAÇÃO', key: 'modulation', value: 'GERACAO' },
  { name: 'FLAT', key: 'modulation', value: 'FLAT' },
  { name: 'CURVA TRD', key: 'modulation', value: 'CURVA_TRD' },
];
const PRICE_TYPE_OPTIONS: CheckboxOptionProps[] = [
  { name: 'FIXO', key: 'priceType', value: 'FIXO' },
  { name: '% PLD', key: 'priceType', value: 'PERCENT_PLD' },
  { name: 'SPREAD', key: 'priceType', value: 'SPREAD' },
];
const MARKET_OPTIONS: CheckboxOptionProps[] = [
  { name: 'INTERNO', key: 'market', value: 'INTERNAL' },
  { name: 'EXTERNO', key: 'market', value: 'EXTERNAL' },
];
const ACCOUNT_TYPES_OPTIONS: CheckboxOptionProps[] = [
  { name: 'Comercializador / Gestor de Energia', value: 'Comercializador / Gestor de Energia', key: 'accountTypes' },
  { name: 'Consumidor', value: 'Consumidor', key: 'accountTypes' },
  { name: 'Distribuidor / Transmissor', value: 'Distribuidor / Transmissor', key: 'accountTypes' },
  { name: 'Gerador', value: 'Gerador', key: 'accountTypes' },
  { name: 'Outras Contas', value: 'Outras Contas', key: 'accountTypes' },
];

function Filter() {
  const { paper, header, label } = useStyles();
  const { setFilter, resetField, removeField, ...state } = usePortfolio();
  const { isOpen, onClose } = usePortfolioFilter();
  const [giro, setGiro] = useState('');
  const { theme } = useColorMode();
  const { operation } = useParams();
  const showUsageFactorFilter = operation !== 'purchase';

  const getSelectedEnergyGroup = () => {
    const currentTypes = state.energyType ?? [];

    if (currentTypes.length === 0) return '';

    const hasAllIncentivized = INCENTIVIZED_ENERGY_TYPES.every((type) => currentTypes.includes(type));
    const hasAllConventional = CONVENTIONAL_ENERGY_TYPES.every((type) => currentTypes.includes(type));
    const hasOnlyIncentivized =
      hasAllIncentivized &&
      currentTypes.length === INCENTIVIZED_ENERGY_TYPES.length &&
      currentTypes.every((type) => INCENTIVIZED_ENERGY_TYPES.includes(type));
    const hasOnlyConventional =
      hasAllConventional &&
      currentTypes.length === CONVENTIONAL_ENERGY_TYPES.length &&
      currentTypes.every((type) => CONVENTIONAL_ENERGY_TYPES.includes(type));

    if (hasOnlyIncentivized) return 'INCENTIVIZED';
    if (hasOnlyConventional) return 'CONVENTIONAL';

    return '';
  };

  const handleEnergyGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedGroup = event.target.value;

    if (selectedGroup === 'INCENTIVIZED') {
      setFilter('energyType', [...INCENTIVIZED_ENERGY_TYPES]);
    } else if (selectedGroup === 'CONVENTIONAL') {
      setFilter('energyType', [...CONVENTIONAL_ENERGY_TYPES]);
    } else {
      setFilter('energyType', []);
    }
  };

  const handleCheckbox =
    <Key extends NonFunctionKeys<PortfolioHookProps>, Value extends PortfolioHookProps[Key]>(key: Key, value: Value) =>
    (_: React.ChangeEvent<object>, checked: boolean) => {
      const currentValue = usePortfolio.getState()[key];

      if (key === 'energyType') {
        const currentTypes = Array.isArray(currentValue) ? currentValue : [];
        const newTypes = checked ? [...currentTypes, value as string] : currentTypes.filter((t) => t !== value);

        setFilter('energyType', newTypes);

        return;
      }

      if (key === 'usageFactorByAngra' || key === 'usageFactor' || key === 'includeSuspendedContracts') value = checked as Value;

      if (Array.isArray(currentValue)) {
        value = (checked ? [...currentValue, value] : currentValue.filter((param) => param !== value)) as Value;
      }

      setFilter(key, value);
    };

  const fetchByGiro = (giro: string | undefined) => () => {
    if (giro) setFilter('giro', giro);
  };
  const onDisable = () => {
    setGiro('');
    resetField('giro');
    removeField('hasGiro');
  };

  const renderCheckboxSummary = (summaryLabel: string, filter: keyof PortfolioProps, options: CheckboxOptionProps[]) => (
    <Summary label={summaryLabel} filter={filter}>
      <FormControl component="fieldset">
        <FormGroup>
          {options.map(({ name, key, value }) => {
            const currentValue = state[key];
            const checked = Array.isArray(currentValue) && (currentValue as string[]).includes(value);

            return (
              <FormControlLabel
                key={`${key}.${value}`}
                classes={{ label }}
                control={<Checkbox color="primary" />}
                label={name}
                checked={checked}
                onChange={handleCheckbox(key as keyof PortfolioProps, value)}
              />
            );
          })}
        </FormGroup>
      </FormControl>
    </Summary>
  );

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      classes={{ paper }}
      open={isOpen}
      style={{
        display: isOpen ? 'flex' : 'none',
        overflow: 'auto',
        position: 'relative',
      }}
      transitionDuration={0}
      elevation={0}
    >
      <Box className={header} position="sticky" top="0" zIndex={theme.zIndex.appBar + 1}>
        <Typography variant="h3">Filtro</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Summary label="Tipo de energia" filter="energyType">
        <Box width="100%" display="flex" flexDirection="column">
          <FormControl component="fieldset">
            <FormLabel component="legend" style={{ fontSize: '10px', lineHeight: '10.8px', fontWeight: 400 }}>
              FONTE
            </FormLabel>
            <RadioGroup value={getSelectedEnergyGroup()} onChange={handleEnergyGroupChange}>
              <FormControlLabel
                classes={{ label }}
                value="INCENTIVIZED"
                control={<Radio color="primary" />}
                label="Incentivada"
              />
              <FormControlLabel
                classes={{ label }}
                value="CONVENTIONAL"
                control={<Radio color="primary" />}
                label="Convencional"
              />
            </RadioGroup>
          </FormControl>
          <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />

          <Box display="flex" flexDirection="row" alignItems="flex-start" justifyContent="space-between">
            <FormControl component="fieldset">
              <FormLabel component="legend" style={{ fontSize: '10px', lineHeight: '10.8px', fontWeight: 400 }}>
                ESPECIAL
              </FormLabel>
              <FormGroup>
                {[
                  { key: 'energyType', value: 'I5' },
                  { key: 'energyType', value: 'I8' },
                  { key: 'energyType', value: 'I1' },
                  { key: 'energyType', value: 'I0' },
                ].map(({ key, value }) => (
                  <FormControlLabel
                    key={`${key}.${value}`}
                    classes={{ label }}
                    control={<Checkbox color="primary" />}
                    label={value}
                    checked={!!state[key]?.includes(value)}
                    onChange={handleCheckbox(key as keyof PortfolioProps, value)}
                  />
                ))}
              </FormGroup>
            </FormControl>
            <FormControl component="fieldset">
              <FormLabel component="legend" style={{ fontSize: '10px', lineHeight: '10.8px', fontWeight: 400 }}>
                NÃO ESPECIAL
              </FormLabel>
              <FormGroup>
                {[
                  { key: 'energyType', value: 'C' },
                  { key: 'energyType', value: 'INE5' },
                ].map(({ key, value }) => (
                  <FormControlLabel
                    key={`${key}.${value}`}
                    classes={{ label }}
                    control={<Checkbox color="primary" />}
                    label={value}
                    checked={!!state[key]?.includes(value)}
                    onChange={handleCheckbox(key as keyof PortfolioProps, value)}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
        </Box>
      </Summary>
      {renderCheckboxSummary('Modulação', 'modulation', MODULATION_OPTIONS)}
      {renderCheckboxSummary('Preço', 'priceType', PRICE_TYPE_OPTIONS)}
      <Summary label="Prazo" filter="periodType">
        <RadioGroup onChange={({ target }) => setFilter('periodType', target.value as PeriodTypes)}>
          <FormControlLabel
            classes={{ label }}
            checked={state.periodType === 'LONG'}
            value="LONG"
            control={<Radio color="primary" />}
            label="Longo"
          />
          <FormControlLabel
            classes={{ label }}
            checked={state.periodType === 'SHORT'}
            value="SHORT"
            control={<Radio color="primary" />}
            label="Curto"
          />
        </RadioGroup>
      </Summary>
      {showUsageFactorFilter && (
        <Summary label="Coeficiente de utilização" filter={['usageFactorByAngra', 'usageFactor']}>
          <FormControl component="fieldset">
            {[
              { key: 'usageFactorByAngra', label: 'Por angra', value: true },
              { key: 'usageFactor', label: 'Por período', value: true },
            ].map(({ key, label, value }) => (
              <FormControlLabel
                key={`${key}.${value}`}
                classes={{ label }}
                control={<Checkbox color="primary" />}
                label={label}
                checked={!!state[key]}
                onChange={handleCheckbox(key as keyof PortfolioProps, value)}
              />
            ))}
          </FormControl>
        </Summary>
      )}
      <Summary label="Giro" filter="hasGiro" onDisable={onDisable}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup onChange={({ target }) => setFilter('hasGiro', target.value == 'com')}>
            <FormControlLabel
              classes={{ label }}
              checked={state.hasGiro === true}
              value="com"
              control={<Radio color="primary" />}
              label="Com"
            />
            <FormControlLabel
              classes={{ label }}
              checked={state.hasGiro == false}
              value="sem"
              control={<Radio color="primary" />}
              label="Sem"
            />
          </RadioGroup>
        </FormControl>
        <FormControl component="fieldset" fullWidth>
          <TextField
            label="Código do Giro"
            name="giro"
            onChange={({ target }) => setGiro(target.value)}
            onBlur={fetchByGiro(giro)}
            value={giro}
            autoComplete="off"
            disabled={state.hasGiro != true}
          />
        </FormControl>
      </Summary>
      {renderCheckboxSummary('Mercado', 'market', MARKET_OPTIONS)}
      <ContractFlagFilter />
      <Summary label="Data de fechamento" filter={['closeDateLte', 'closeDateGte']}>
        <FormControl component="fieldset" fullWidth>
          <DatePicker
            value={state.closeDateGte ? DateTime.fromISO(state.closeDateGte) : null}
            onChange={(date) => setFilter('closeDateGte', DateTime.fromJSDate(date as Date).toISODate())}
            inputVariant="outlined"
            variant="dialog"
            label="Inicio"
            format="dd/MM/yyyy"
            color="secondary"
            cancelLabel="cancelar"
            name="closeDateGte"
            id="closeDateGte"
            style={{
              marginBottom: 8,
            }}
            TextFieldComponent={TextField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FiCalendar />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          <DatePicker
            value={state.closeDateLte ? DateTime.fromISO(state.closeDateLte) : null}
            onChange={(date) => setFilter('closeDateLte', DateTime.fromJSDate(date as Date).toISODate())}
            inputVariant="outlined"
            variant="dialog"
            label="Fim"
            format="dd/MM/yyyy"
            color="secondary"
            cancelLabel="cancelar"
            name="closeDateLte"
            id="closeDateLte"
            TextFieldComponent={TextField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FiCalendar />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </FormControl>
      </Summary>
      <Summary label="P. Contábil" filter="accountingPortfolio">
        <RadioGroup onChange={({ target }) => setFilter('accountingPortfolio', target.value as AccountingPortfolioTypes)}>
          <FormControlLabel
            classes={{ label }}
            checked={state.accountingPortfolio === 'WHOLESALE'}
            value="WHOLESALE"
            control={<Radio color="primary" />}
            label="Atacado"
          />
          <FormControlLabel
            classes={{ label }}
            checked={state.accountingPortfolio === 'TRADING'}
            value="TRADING"
            control={<Radio color="primary" />}
            label="Negociação"
          />
        </RadioGroup>
      </Summary>
      {renderCheckboxSummary('Tipo da conta', 'accountTypes', ACCOUNT_TYPES_OPTIONS)}
      <Summary label="Contratos suspensos" filter="includeSuspendedContracts">
        <FormControl component="fieldset">
          <FormControlLabel
            classes={{ label }}
            control={<Checkbox color="primary" />}
            label="Incluir contratos suspensos"
            checked={!!state.includeSuspendedContracts}
            onChange={handleCheckbox('includeSuspendedContracts', true)}
          />
        </FormControl>
      </Summary>
    </Drawer>
  );
}

export default Filter;
